/* Browser push client — talks to /api/push/* and manages the Service Worker
 * subscription state. Exposes a tiny hook + helper for use in toggles/banners.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function isPushSupported() {
  if (typeof window === "undefined") return false;
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

export function getPermissionState() {
  if (typeof window === "undefined" || !("Notification" in window)) return "unsupported";
  return Notification.permission; // 'default' | 'granted' | 'denied'
}

async function registerSW() {
  if (!("serviceWorker" in navigator)) return null;
  return navigator.serviceWorker.register("/sw.js");
}

export async function subscribeToPush() {
  if (!isPushSupported()) {
    throw new Error("Push notifications aren't supported in this browser.");
  }
  // Ask permission (idempotent — granted/denied returns immediately)
  let perm = Notification.permission;
  if (perm === "default") {
    perm = await Notification.requestPermission();
  }
  if (perm !== "granted") {
    throw new Error(
      perm === "denied"
        ? "Permission was denied. Re-enable notifications in your browser settings."
        : "Permission required."
    );
  }
  const { data: cfg } = await axios.get(`${API}/push/config`);
  if (!cfg?.enabled || !cfg?.vapid_public_key) {
    throw new Error("Push is not configured on the server.");
  }
  const reg = await registerSW();
  if (!reg) throw new Error("Service Worker registration failed.");
  // Reuse existing subscription if it matches our VAPID key; otherwise re-create.
  let sub = await reg.pushManager.getSubscription();
  const applicationServerKey = urlBase64ToUint8Array(cfg.vapid_public_key);
  if (sub) {
    // If keys differ (server rotated VAPID), unsubscribe and re-create
    try {
      const existing = sub.options?.applicationServerKey;
      const same = existing && new Uint8Array(existing).every((b, i) => b === applicationServerKey[i]);
      if (!same) {
        await sub.unsubscribe();
        sub = null;
      }
    } catch {
      // Best-effort; just continue
    }
  }
  if (!sub) {
    sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey });
  }
  await axios.post(`${API}/push/subscribe`, { subscription: sub.toJSON() });
  return sub;
}

export async function unsubscribeFromPush() {
  try {
    const reg = await registerSW();
    if (!reg) return;
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      try {
        await axios.post(`${API}/push/unsubscribe`, { endpoint: sub.endpoint });
      } catch {
        // Continue with browser-side unsubscribe even if server unreachable
      }
      await sub.unsubscribe();
    }
    // Mirror on the user as opted-out so future re-installs default off.
    try {
      await axios.post(`${API}/push/prefs`, { opt_out_all: true });
    } catch {
      // ignore
    }
  } catch (e) {
    // Surface to caller
    throw e;
  }
}

export async function sendTestPush() {
  await axios.post(`${API}/push/test`);
}

/* Hook — reports current subscription + permission state, refreshes on focus. */
export function usePushState() {
  const [perm, setPerm] = useState(getPermissionState());
  const [subscribed, setSubscribed] = useState(false);
  const [supported, setSupported] = useState(isPushSupported());

  const refresh = useCallback(async () => {
    setSupported(isPushSupported());
    setPerm(getPermissionState());
    if (!("serviceWorker" in navigator)) return;
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      setSubscribed(!!sub);
    } catch {
      setSubscribed(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refresh]);

  return { perm, subscribed, supported, refresh };
}
